import { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import isNumber from 'lodash/isNumber';

// app
import { EditRiskQuoteView } from './EditRiskQuote.view';
import { usePatchQuote } from 'lib/quoteBind';
import { RISK_QUOTE_STATUS_QUOTED } from 'consts';
import * as utils from 'utils';

EditRiskQuote.propTypes = {
  quote: PropTypes.object.isRequired,
  handleClose: PropTypes.func,
};

export function EditRiskQuote({ quote, handleClose }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: patchRiskQuote } = usePatchQuote();

  const quoteStatus = quote.response ? quote.response.responseStatus : quote.status;
  const isQuoted = quoteStatus === RISK_QUOTE_STATUS_QUOTED;

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    const result = await patchRiskQuote({ formData: data, quote, accept: false });
    if (result) handleCancel();
    else setIsSubmitting(false);
  };

  const handleSubmitAndAccept = async (data) => {
    setIsSubmitting(true);
    const result = await patchRiskQuote({ formData: data, quote, accept: true });
    if (result) handleCancel();
    else setIsSubmitting(false);
  };

  const handleCancel = () => {
    if (utils.generic.isFunction(handleClose)) {
      handleClose();
    }
  };

  // abort
  if (!quote || !quote.id) return null;

  const fields = [
    [
      {
        gridSize: { xs: 12, sm: 6 },
        type: 'number',
        name: 'grossPremium',
        value: isNumber(quote?.commission?.revisedPremium) ? quote.commission.revisedPremium : quote.grossPremium,
        label: `${utils.string.t('risks.grossPremium')}${quote?.currency ? ` (${quote?.currency.trim()})` : ``}`,
        validation: Yup.number()
          .nullable()
          .transform(function (value, originalvalue) {
            return this.isType(value) ? (Number.isNaN(value) ? null : value) : null;
          })
          .currency()
          .required(utils.string.t('validation.required')),
        disabled: isSubmitting,
      },
      {
        gridSize: { xs: 12, sm: 6 },
        type: 'datepicker',
        name: 'validUntil',
        label: utils.string.t('risks.quoteValidUntil.label'),
        value: quote.validUntil,
        outputFormat: 'iso',
        muiPickerProps: {
          minDate: utils.date.tomorrow(),
        },
        muiComponentProps: {
          fullWidth: true,
          disabled: isSubmitting,
        },
        validation: Yup.string().nullable().required(utils.string.t('risks.quoteValidUntil.required')),
      },
    ],
    {
      type: 'hidden',
      name: 'riskId',
      value: quote.riskId,
    },
  ];

  const updateAndAccept = {
    name: 'secondary',
    label: utils.string.t('risks.updateAndAccept'),
    handler: handleSubmitAndAccept,
    disabled: isSubmitting,
    tooltip: {
      title: utils.string.t('risks.updateLegend.submitAccept'),
    },
  };

  const actions = [
    {
      name: 'submit',
      label: utils.string.t('risks.update'),
      handler: handleSubmit,
      disabled: isSubmitting,
      tooltip: {
        title: utils.string.t('risks.updateLegend.submit'),
      },
    },
    {
      name: 'cancel',
      label: utils.string.t('app.cancel'),
      handler: handleCancel,
      disabled: isSubmitting,
    },
  ];

  return <EditRiskQuoteView fields={fields} actions={isQuoted ? actions : [updateAndAccept, ...actions]} />;
}

export default EditRiskQuote;
